import { Skeleton } from '@/components/ui/skeleton';
import { PageWrapper } from '@/components/layout';

/**
 * Skeleton for the Index/Homepage.
 * Reserves ~1400px total height to prevent CLS.
 *
 * Layout breakdown:
 * - Hero section: ~400px
 * - Pipeline activity: ~200px
 * - Recent activity carousel: ~300px
 * - Search section: ~200px
 * - Feature showcase: ~300px
 */
export function IndexPageSkeleton() {
  return (
    <PageWrapper customContainer={true} variant="fullwidth">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-secondary-50 via-background to-warning-50 dark:from-[#071320] dark:via-dark-base dark:to-[#122a3a]">
        <div className="container mx-auto px-4 max-w-[1440px]">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 md:gap-6 lg:gap-12 py-16 md:py-24">
            {/* Left content */}
            <div className="flex flex-col items-start max-w-2xl md:max-w-sm lg:max-w-2xl md:flex-1 text-center md:text-left">
              {/* Badge */}
              <Skeleton className="h-8 w-64 rounded-full mb-6" />

              {/* Headline */}
              <Skeleton className="h-12 w-full sm:h-16 mb-2" />
              <Skeleton className="h-12 w-4/5 sm:h-16 mb-6" />

              {/* Subheading */}
              <Skeleton className="h-6 w-full max-w-xl mb-2" />
              <Skeleton className="h-6 w-3/4 max-w-lg mb-8" />

              {/* Benefits */}
              <div className="flex flex-wrap gap-4 mb-10">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-28" />
                <Skeleton className="h-5 w-36" />
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Skeleton className="h-14 w-36 rounded-xl" />
                <Skeleton className="h-14 w-32 rounded-xl" />
              </div>

              {/* Tour button */}
              <Skeleton className="h-10 w-56 rounded-full mt-6" />
            </div>

            {/* Right content - Activity card */}
            <div className="w-full md:w-[360px] lg:w-[420px]">
              <div className="bg-card border border-border rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b border-border">
                  <Skeleton className="h-5 w-32" />
                </div>
                <div className="p-4 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                      <Skeleton className="h-3 w-12" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pipeline Activity Section */}
      <section className="py-8 md:py-12 bg-muted/30">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-6">
            <Skeleton className="h-6 w-48 mx-auto mb-2" />
            <Skeleton className="h-4 w-64 mx-auto" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-4">
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Activity Carousel Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-background to-neutral-50/80 dark:to-neutral-900/80">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-8">
            <Skeleton className="h-8 w-64 mx-auto mb-2" />
            <Skeleton className="h-4 w-80 mx-auto" />
          </div>
          {/* Carousel cards */}
          <div className="flex gap-4 overflow-hidden mx-10">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] rounded-xl border border-border bg-card p-4 min-h-[140px]"
              >
                <div className="flex justify-between items-start mb-3">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                </div>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3 mb-3" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <Skeleton className="w-6 h-2 rounded-full" />
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="w-2 h-2 rounded-full" />
          </div>
          {/* CTA */}
          <div className="text-center mt-8">
            <Skeleton className="h-10 w-32 mx-auto" />
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 md:py-16 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Skeleton className="h-8 w-48 mx-auto mb-4" />
          <Skeleton className="h-5 w-96 mx-auto mb-8" />
          <Skeleton className="h-14 w-full max-w-2xl mx-auto rounded-xl" />
        </div>
      </section>

      {/* Feature Showcase Section */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-8">
            <Skeleton className="h-8 w-56 mx-auto mb-2" />
            <Skeleton className="h-4 w-72 mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-6">
                <Skeleton className="h-12 w-12 rounded-lg mb-4" />
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
